import { ServiceError } from '../../contracts';
import type { AdminBannerService, AdminCampaignService, AdminCouponService, AdminOfferService } from '../../adminContracts';
import { anchorTime, ctx, DAY_MS, delay, hydrateCustomers, matches, nowIso, paginate, saveAdmin, uid } from './context';

const invalid = (m: string) => new ServiceError(m, 'INVALID');
const notFound = (w: string) => new ServiceError(`${w} not found`, 'NOT_FOUND');
const ts = (s?: string) => (s ? Date.parse(s) : NaN);

export const adminCoupons: AdminCouponService = {
  async list(q) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    const now = Date.now();
    const state = (c: (typeof d.coupons)[number]) =>
      Date.parse(c.expiresAt) < now || c.usedCount >= c.usageLimit ? 'expired' : c.enabled ? 'active' : 'disabled';
    const rows = d.coupons.filter((c) => matches(q.q, c.code, c.title) && (!q.state || q.state === 'all' || state(c) === q.state))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async create(input) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    const code = input.code.trim().toUpperCase();
    validateCoupon(d.coupons.map((c) => c.code), code, input);
    const c = { ...input, code, id: uid('cpn'), tenantId: d.tenantId, usedCount: 0, createdAt: nowIso() };
    d.coupons.unshift(c);
    saveAdmin();
    return delay(c, 500);
  },
  async update(id, input) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    const i = d.coupons.findIndex((c) => c.id === id);
    if (i < 0) throw notFound('Coupon');
    const code = input.code.trim().toUpperCase();
    validateCoupon(d.coupons.filter((c) => c.id !== id).map((c) => c.code), code, input);
    if (input.usageLimit < d.coupons[i].usedCount) throw invalid(`Usage limit cannot be lower than the ${d.coupons[i].usedCount} redemptions already used`);
    d.coupons[i] = { ...d.coupons[i], ...input, code };
    saveAdmin();
    return delay(d.coupons[i], 500);
  },
  async setEnabled(id, enabled) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    const c = d.coupons.find((x) => x.id === id);
    if (!c) throw notFound('Coupon');
    c.enabled = enabled;
    saveAdmin();
    return delay(c, 250);
  },
  async remove(id) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    const c = d.coupons.find((x) => x.id === id);
    if (!c) throw notFound('Coupon');
    if (c.usedCount > 0) throw new ServiceError('This coupon has been redeemed, so it cannot be deleted. Disable it instead.', 'CONFLICT');
    d.coupons = d.coupons.filter((x) => x.id !== id);
    saveAdmin();
    return delay(undefined, 300);
  },
  async usage(id) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    const c = d.coupons.find((x) => x.id === id);
    if (!c) throw notFound('Coupon');
    const rows = d.orders.filter((o) => o.couponCode === c.code).sort((a, b) => b.placedAt.localeCompare(a.placedAt)).slice(0, 25)
      .map((o) => ({ orderNumber: o.number, customerName: o.customer.name, discount: o.pricing.couponDiscount, at: o.placedAt }));
    return delay(rows, 300);
  },
};

function validateCoupon(existing: string[], code: string, i: Parameters<AdminCouponService['create']>[0]) {
  if (!/^[A-Z0-9]{3,20}$/.test(code)) throw invalid('Code must be 3–20 letters or digits');
  if (existing.includes(code)) throw new ServiceError('A coupon with this code already exists', 'CONFLICT');
  if (!i.title.trim()) throw invalid('Give the coupon a title');
  if (i.type === 'PERCENTAGE' && !(i.value > 0 && i.value <= 100)) throw invalid('Percentage must be between 1 and 100');
  if (i.type === 'FIXED' && !(i.value > 0)) throw invalid('Discount amount must be above zero');
  if (i.minOrder < 0 || i.usageLimit < 1 || i.perCustomerLimit < 1) throw invalid('Minimum order, usage limit and per-customer limit must be valid numbers');
  if (i.type === 'FIXED' && i.minOrder && i.value > i.minOrder) throw invalid('A fixed discount cannot exceed the minimum order value');
  if (!i.expiresAt) throw invalid('Choose an expiry date');
  if (i.startsAt && ts(i.startsAt) >= ts(i.expiresAt)) throw invalid('Start date must be before the expiry date');
}

export const adminBanners: AdminBannerService = {
  async list() {
    const { data: d } = ctx('marketing.manage', 'banners');
    return delay([...d.banners].sort((a, b) => a.sortOrder - b.sortOrder));
  },
  async create(input) {
    const { data: d } = ctx('marketing.manage', 'banners');
    validateBanner(input);
    const b = { ...input, id: uid('ban'), tenantId: d.tenantId, sortOrder: d.banners.length };
    d.banners.push(b);
    saveAdmin();
    return delay(b, 500);
  },
  async update(id, input) {
    const { data: d } = ctx('marketing.manage', 'banners');
    const i = d.banners.findIndex((b) => b.id === id);
    if (i < 0) throw notFound('Banner');
    validateBanner(input);
    d.banners[i] = { ...d.banners[i], ...input };
    saveAdmin();
    return delay(d.banners[i], 500);
  },
  async setEnabled(id, enabled) {
    const { data: d } = ctx('marketing.manage', 'banners');
    const b = d.banners.find((x) => x.id === id);
    if (!b) throw notFound('Banner');
    if (enabled === false && b.placement === 'HERO' && d.banners.filter((x) => x.placement === 'HERO' && x.enabled).length <= 1) {
      throw new ServiceError('Keep at least one active hero banner — the storefront homepage needs it.', 'CONFLICT');
    }
    b.enabled = enabled;
    saveAdmin();
    return delay(b, 250);
  },
  async remove(id) {
    const { data: d } = ctx('marketing.manage', 'banners');
    const b = d.banners.find((x) => x.id === id);
    if (!b) throw notFound('Banner');
    if (b.placement === 'HERO' && d.banners.filter((x) => x.placement === 'HERO').length <= 1) throw new ServiceError('You cannot delete the only hero banner.', 'CONFLICT');
    d.banners = d.banners.filter((x) => x.id !== id);
    saveAdmin();
    return delay(undefined, 300);
  },
};

function validateBanner(i: Parameters<AdminBannerService['create']>[0]) {
  if (!i.title.trim()) throw invalid('Banner title is required');
  if (!i.cta.label.trim() || !/^(\/|https?:\/\/)/.test(i.cta.to)) throw invalid('Button label and a link starting with "/" or "https://" are required');
  if (i.imageUrl && !/^(https?:\/\/|data:image\/)/.test(i.imageUrl)) throw invalid('Image must be an http(s) URL or an uploaded image');
  if (i.startsAt && i.endsAt && ts(i.startsAt) >= ts(i.endsAt)) throw invalid('Schedule end must be after the start');
}

export const adminOffers: AdminOfferService = {
  async list() {
    const { data: d } = ctx('marketing.manage', 'coupons');
    return delay([...d.offers].sort((a, b) => a.priority - b.priority));
  },
  async create(input) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    validateOffer(input);
    const o = { ...input, id: uid('off'), tenantId: d.tenantId };
    d.offers.push(o);
    saveAdmin();
    return delay(o, 500);
  },
  async update(id, input) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    const i = d.offers.findIndex((o) => o.id === id);
    if (i < 0) throw notFound('Offer');
    validateOffer(input);
    d.offers[i] = { ...d.offers[i], ...input };
    saveAdmin();
    return delay(d.offers[i], 500);
  },
  async setEnabled(id, enabled) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    const o = d.offers.find((x) => x.id === id);
    if (!o) throw notFound('Offer');
    o.enabled = enabled;
    saveAdmin();
    return delay(o, 250);
  },
  async remove(id) {
    const { data: d } = ctx('marketing.manage', 'coupons');
    if (!d.offers.some((o) => o.id === id)) throw notFound('Offer');
    d.offers = d.offers.filter((o) => o.id !== id);
    saveAdmin();
    return delay(undefined, 300);
  },
};

function validateOffer(i: Parameters<AdminOfferService['create']>[0]) {
  if (!i.name.trim()) throw invalid('Offer name is required');
  if (i.type === 'PERCENT_OFF' && !(i.value > 0 && i.value <= 100)) throw invalid('Percentage must be between 1 and 100');
  if ((i.type === 'FLAT_OFF' || i.type === 'BUY_X_GET_Y') && !(i.value > 0)) throw invalid('Value must be above zero');
  if (!i.startsAt || !i.endsAt || ts(i.startsAt) >= ts(i.endsAt)) throw invalid('The end date must be after the start date');
}

export const adminCampaigns: AdminCampaignService = {
  async list(q) {
    const { data: d } = ctx('marketing.manage');
    return delay(d.campaigns.filter((c) => !q?.status || q.status === 'all' || c.status === q.status).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
  },
  async create(input) {
    const { data: d } = ctx('marketing.manage');
    validateCampaign(d.coupons.map((c) => c.code), input);
    const c = {
      ...input, id: uid('cmp'), tenantId: d.tenantId, status: input.scheduledAt ? ('SCHEDULED' as const) : ('DRAFT' as const),
      stats: { recipients: 0, opened: 0, clicked: 0, conversions: 0 }, createdAt: nowIso(),
    };
    d.campaigns.unshift(c);
    saveAdmin();
    return delay(c, 500);
  },
  async update(id, input) {
    const { data: d } = ctx('marketing.manage');
    const i = d.campaigns.findIndex((c) => c.id === id);
    if (i < 0) throw notFound('Campaign');
    if (!['DRAFT', 'SCHEDULED'].includes(d.campaigns[i].status)) throw new ServiceError('Sent or cancelled campaigns cannot be edited.', 'CONFLICT');
    validateCampaign(d.coupons.map((c) => c.code), input);
    d.campaigns[i] = { ...d.campaigns[i], ...input, status: input.scheduledAt ? 'SCHEDULED' : 'DRAFT' };
    saveAdmin();
    return delay(d.campaigns[i], 500);
  },
  async send(id) {
    const { data: d } = ctx('marketing.manage');
    const c = d.campaigns.find((x) => x.id === id);
    if (!c) throw notFound('Campaign');
    if (!['DRAFT', 'SCHEDULED'].includes(c.status)) throw new ServiceError('This campaign has already been sent or cancelled.', 'CONFLICT');
    const n = audience(d, c.audience);
    if (n === 0) throw new ServiceError('This audience has no customers yet.', 'CONFLICT');
    // MOCK: nothing is delivered. Real sending = provider integration + queue worker.
    c.status = 'SENT';
    c.sentAt = nowIso();
    c.stats = { recipients: n, opened: Math.round(n * 0.52), clicked: Math.round(n * 0.2), conversions: Math.round(n * 0.05) };
    saveAdmin();
    return delay(c, 900);
  },
  async cancel(id) {
    const { data: d } = ctx('marketing.manage');
    const c = d.campaigns.find((x) => x.id === id);
    if (!c) throw notFound('Campaign');
    if (!['DRAFT', 'SCHEDULED'].includes(c.status)) throw new ServiceError('Only draft or scheduled campaigns can be cancelled.', 'CONFLICT');
    c.status = 'CANCELLED';
    c.scheduledAt = undefined;
    saveAdmin();
    return delay(c, 300);
  },
  async remove(id) {
    const { data: d } = ctx('marketing.manage');
    const c = d.campaigns.find((x) => x.id === id);
    if (!c) throw notFound('Campaign');
    if (c.status === 'SENT') throw new ServiceError('Sent campaigns are kept for reporting and cannot be deleted.', 'CONFLICT');
    d.campaigns = d.campaigns.filter((x) => x.id !== id);
    saveAdmin();
    return delay(undefined, 300);
  },
  async audienceSize(a) {
    const { data: d } = ctx('marketing.manage');
    return delay(audience(d, a), 150);
  },
};

function audience(d: ReturnType<typeof ctx>['data'], a: string) {
  const anchor = anchorTime(d);
  return hydrateCustomers(d).filter((c) => c.status === 'ACTIVE' && (
    a === 'ALL' ? true : a === 'NEW' ? c.tags.includes('New') : a === 'VIP' ? c.tags.includes('VIP') : a === 'WALLET_HOLDERS' ? c.walletBalance > 0
      : anchor - Date.parse(c.lastActiveAt) > 45 * DAY_MS)).length;
}

function validateCampaign(codes: string[], i: Parameters<AdminCampaignService['create']>[0]) {
  if (!i.name.trim()) throw invalid('Campaign name is required');
  if (!i.message.trim()) throw invalid('Write a message');
  if (i.channel === 'EMAIL' && !i.subject.trim()) throw invalid('Email campaigns need a subject');
  if (i.linkedCouponCode && !codes.includes(i.linkedCouponCode)) throw invalid('The linked coupon does not exist');
  if (i.scheduledAt && ts(i.scheduledAt) < Date.now() - 60_000) throw invalid('Choose a schedule time in the future');
}
